// @ts-nocheck
import { useCallback, useEffect, useMemo, useState } from 'react'

import api from './api/api'
import CreateRequestModal from './components/CreateRequestModal'
import RequestWorkPanel from './components/RequestWorkPanel'
import RequestsAgGrid from './components/RequestsAgGrid'
import { getStoredAccess, isBusinessAdmin } from '../../service/authAccess'

const STATUS_FILTERS = [
  { key: '', label: 'Всего', color: '#eef4ff', border: '#b2ccff', text: '#175cd3' },
  { key: 'NEW', label: 'Новые', color: '#e0f2fe', border: '#7dd3fc', text: '#075985' },
  { key: 'ACCEPTED', label: 'Принято', color: '#fff7d6', border: '#f6d365', text: '#8a5a00' },
  { key: 'WAITING_CONFIRMATION', label: 'В ожидании', color: '#fff4e5', border: '#ffbd5a', text: '#9a4b00' },
  { key: 'DONE', label: 'Выполнено', color: '#e7f7ed', border: '#8fd6a8', text: '#1f7a3f' },
  { key: 'REJECTED', label: 'Отклонено', color: '#f2f4f7', border: '#cfd4dc', text: '#475467' },
  { key: 'CANCELED', label: 'Отозвано', color: '#f2f4f7', border: '#cfd4dc', text: '#475467' },
]

const MONTHS = [
  { value: '', label: 'Все месяцы' },
  { value: '1', label: 'Январь' },
  { value: '2', label: 'Февраль' },
  { value: '3', label: 'Март' },
  { value: '4', label: 'Апрель' },
  { value: '5', label: 'Май' },
  { value: '6', label: 'Июнь' },
  { value: '7', label: 'Июль' },
  { value: '8', label: 'Август' },
  { value: '9', label: 'Сентябрь' },
  { value: '10', label: 'Октябрь' },
  { value: '11', label: 'Ноябрь' },
  { value: '12', label: 'Декабрь' },
]


function PageControlRequests() {
  const [requests, setRequests] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [showClosed, setShowClosed] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [periodYear, setPeriodYear] = useState(() => String(new Date().getFullYear()))
  const [periodMonth, setPeriodMonth] = useState(() => String(new Date().getMonth() + 1))
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null)
  const [graphRefreshKey, setGraphRefreshKey] = useState(0)

  const user = useMemo(() => {
    const username = localStorage.getItem('username') || ''
    const access = getStoredAccess()

    return {
      username,
      is_admin: isBusinessAdmin(access),
      org: '',
      org_name: '',
    }
  }, [])

  const loadRequests = useCallback(async () => {
    try {
      setIsRefreshing(true)

      const response = await api.get('requests/', {
        params: {
          requested_year: periodYear || undefined,
          requested_month: periodMonth || undefined,
        },
      })
      const data = response.data.results || response.data

      const sorted = [...data].sort((a, b) => (
        (a.org_name || '').localeCompare(b.org_name || '', 'ru')
      ))

      setRequests(sorted)
      setSelectedRequest((current) => {
        if (!current) return null
        const listItem = sorted.find((item) => item.id === current.id)
        if (!listItem) return null

        return {
          ...current,
          ...listItem,
          comments: current.comments,
          email_notifications: current.email_notifications,
        }
      })
      setLastUpdatedAt(new Date())
      setGraphRefreshKey((value) => value + 1)

      return sorted
    } catch (error) {
      console.error(error)
      return []
    } finally {
      setIsRefreshing(false)
    }
  }, [periodYear, periodMonth])

  useEffect(() => {
    document.title = 'АСПО.Контроль | Заявки КС'
    loadRequests()
    const timer = window.setInterval(loadRequests, 600000)

    return () => window.clearInterval(timer)
  }, [loadRequests])

  async function loadRequestDetail(requestId, fallbackRequest = null) {
    if (fallbackRequest) {
      setSelectedRequest(fallbackRequest)
    }

    try {
      const response = await api.get(`requests/${requestId}/`)
      setSelectedRequest(response.data)
      return response.data
    } catch (error) {
      console.error(error)
      return fallbackRequest
    }
  }

  function selectRequest(request) {
    if (!request?.id) {
      setSelectedRequest(null)
      return
    }

    if (selectedRequest?.id === request.id && selectedRequest?.comments) {
      return
    }

    loadRequestDetail(request.id, request)
  }

  async function refreshRequestsAndSelected() {
    const currentId = selectedRequest?.id
    await loadRequests()

    if (currentId) {
      await loadRequestDetail(currentId)
    }
  }

  const statusCounts = useMemo(() => {
    const counts = requests.reduce((acc, request) => {
      acc[request.status] = (acc[request.status] || 0) + 1
      return acc
    }, {})

    counts.total = requests.length
    return counts
  }, [requests])

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const years = new Set([currentYear - 3, currentYear - 2, currentYear - 1, currentYear, currentYear + 1])

    requests.forEach((request) => {
      const year = Number(String(request.requested_date || '').slice(0, 4))
      if (year) {
        years.add(year)
      }
    })

    return Array.from(years).sort((a, b) => b - a)
  }, [requests])

  const visibleRequests = requests.filter((request) => {
    if (statusFilter) {
      return request.status === statusFilter
    }

    if (!showClosed && ['DONE', 'REJECTED', 'CANCELED'].includes(request.status)) {
      return false
    }

    return true
  })

  return (
    <div style={pageStyle}>
      <div style={topBarStyle}>
        <h2 style={titleStyle}>Заявки на контрольный спуск</h2>

        {user.is_admin && (
          <button
            type="button"
            style={primaryButtonStyle}
            onClick={() => setIsModalOpen(true)}
          >
            Создать заявку
          </button>
        )}
      </div>

      <div style={toolbarStyle}>
        <div style={periodControlsStyle}>
          <label style={fieldLabelStyle}>
            Год
            <select
              value={periodYear}
              onChange={(event) => setPeriodYear(event.target.value)}
              style={selectStyle}
            >
              {yearOptions.map((year) => (
                <option key={year} value={String(year)}>{year}</option>
              ))}
            </select>
          </label>
          <label style={fieldLabelStyle}>
            Месяц
            <select
              value={periodMonth}
              onChange={(event) => setPeriodMonth(event.target.value)}
              style={selectStyle}
            >
              {MONTHS.map((month) => (
                <option key={month.value || 'all'} value={month.value}>{month.label}</option>
              ))}
            </select>
          </label>
          <label style={checkboxLabelStyle}>
            <input
              type="checkbox"
              checked={showClosed}
              onChange={(event) => setShowClosed(event.target.checked)}
              disabled={Boolean(statusFilter)}
            />
            Показывать закрытые
          </label>
        </div>
        <div style={refreshGroupStyle}>
          <span style={refreshTextStyle}>
            {lastUpdatedAt ? `Обновлено: ${formatTime(lastUpdatedAt)}` : ''}
          </span>
          <button type="button" style={refreshButtonStyle} onClick={loadRequests} disabled={isRefreshing}>
            {isRefreshing ? 'Обновляем...' : 'Обновить'}
          </button>
        </div>
      </div>

      <div style={counterPanelStyle}>
        {STATUS_FILTERS.map((item) => {
          const value = item.key ? Number(statusCounts[item.key] || 0) : Number(statusCounts.total || 0)
          const isActive = statusFilter === item.key

          return (
            <button
              key={item.key || 'total'}
              type="button"
              style={counterButtonStyle(item, isActive)}
              onClick={() => setStatusFilter((current) => (current === item.key ? '' : item.key))}
            >
              <span style={counterLabelStyle}>{item.label}</span>
              <span style={counterValueStyle}>{value}</span>
            </button>
          )
        })}
      </div>

      <RequestsAgGrid
        requests={visibleRequests}
        onSelectRequest={selectRequest}
      />

      <RequestWorkPanel
        key={selectedRequest?.id || 'empty'}
        user={user}
        requests={requests}
        selectedRequest={selectedRequest}
        onSelectRequest={selectRequest}
        onUpdated={refreshRequestsAndSelected}
        refreshKey={graphRefreshKey}
      />

      {isModalOpen && (
        <CreateRequestModal
          user={user}
          onClose={() => setIsModalOpen(false)}
          onCreated={loadRequests}
        />
      )}
    </div>
  )
}

function formatTime(value) {
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(value)
}

const pageStyle = {
  padding: '14px',
  height: 'calc(100vh - 70px)',
  overflow: 'auto',
  boxSizing: 'border-box',
  background: '#f5f7fa',
}

const topBarStyle = {
  background: '#ffffff',
  border: '1px solid #cfcfcf',
  padding: '10px',
  marginBottom: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
}

const titleStyle = {
  margin: 0,
  fontSize: '18px',
}

const primaryButtonStyle = {
  height: '32px',
  padding: '0 14px',
  background: '#1976d2',
  color: '#fff',
  border: '1px solid #0d47a1',
  borderRadius: '3px',
  cursor: 'pointer',
}

const toolbarStyle = {
  background: '#ffffff',
  border: '1px solid #cfcfcf',
  padding: '8px 10px',
  marginBottom: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
}

const periodControlsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexWrap: 'wrap',
}

const fieldLabelStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '13px',
  color: '#344054',
}

const selectStyle = {
  height: '30px',
  minWidth: '120px',
  border: '1px solid #b8c0cc',
  borderRadius: '4px',
  padding: '0 8px',
  background: '#fff',
}

const checkboxLabelStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '13px',
}

const refreshGroupStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}

const refreshTextStyle = {
  color: '#667085',
  fontSize: '12px',
}

const counterPanelStyle = {
  background: '#ffffff',
  border: '1px solid #cfcfcf',
  padding: '8px 10px',
  marginBottom: '10px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
}

const counterButtonStyle = (item, active) => ({
  minWidth: '112px',
  height: '44px',
  padding: '5px 10px',
  border: `1px solid ${active ? item.text : item.border}`,
  borderRadius: '6px',
  background: active ? item.text : item.color,
  color: active ? '#ffffff' : item.text,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  boxShadow: active ? '0 1px 3px rgba(16, 24, 40, 0.18)' : 'none',
})

const counterLabelStyle = {
  fontSize: '12px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
}

const counterValueStyle = {
  fontSize: '18px',
  fontWeight: 800,
}

const refreshButtonStyle = {
  height: '28px',
  padding: '0 10px',
  background: '#ffffff',
  border: '1px solid #b8c0cc',
  borderRadius: '3px',
  cursor: 'pointer',
}

export default PageControlRequests
